import * as React from 'react';
import PropTypes from 'prop-types';
import { Popover, Box, Menu, MenuList, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';

/**
 * TransparentPortal — a reusable glassy popover container
 *
 * Props
 * - open: boolean
 * - onClose: () => void
 * - anchorEl?: HTMLElement | null
 * - anchorPosition?: { top:number, left:number }
 * - anchorOrigin?: Popover['props']['anchorOrigin']
 * - transformOrigin?: Popover['props']['transformOrigin']
 * - children: ReactNode (your content)
 * - minWidth?: number (default 200)
 * - padding?: number (default 0.5) — paper padding in theme spacing units
 * - radius?: number (default 2)
 * - blur?: number (default 16) — px
 * - opacityTop?: number (default 0.30)
 * - opacityBottom?: number (default 0.14)
 * - borderAlpha?: number (default 0.30)
 * - elevationShadow?: string (override box-shadow)
 * - withArrow?: boolean (default false)
 * - offsetY?: number (default 0)
 */
export function TransparentPortal({
  open,
  onClose,
  anchorEl = null,
  anchorPosition,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
  children,
  minWidth = 200,
  padding = 0.5,
  radius = 2,
  blur = 16,
  opacityTop = 0.30,
  opacityBottom = 0.14,
  borderAlpha = 0.30,
  elevationShadow,
  withArrow = false,
  offsetY = 0,
}) {
  const paperSx = React.useMemo(() => ({
    p: padding,
    borderRadius: radius,
    boxShadow: elevationShadow || '0 16px 32px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.28)',
    backdropFilter: `saturate(140%) blur(${blur}px)`,
    WebkitBackdropFilter: `saturate(140%) blur(${blur}px)`,
    background: `linear-gradient(180deg, rgba(255,255,255,${opacityTop}) 0%, rgba(255,255,255,${opacityBottom}) 100%)`,
    border: '1px solid',
    borderColor: `rgba(255,255,255,${borderAlpha})`,
    backgroundClip: 'padding-box',
    minWidth,
    position: 'relative',
    ...(offsetY ? { mt: offsetY / 8 } : {}),
    ...(withArrow
      ? {
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -6,
            left: 16,
            width: 10,
            height: 10,
            transform: 'rotate(45deg)',
            background: `linear-gradient(180deg, rgba(255,255,255,${opacityTop}) 0%, rgba(255,255,255,${opacityBottom}) 100%)`,
            borderLeft: `1px solid rgba(255,255,255,${borderAlpha})`,
            borderTop: `1px solid rgba(255,255,255,${borderAlpha})`,
            boxShadow: '0 1px 0 rgba(255,255,255,0.28) inset',
            backdropFilter: `saturate(140%) blur(${blur}px)`,
            WebkitBackdropFilter: `saturate(140%) blur(${blur}px)`,
          },
        }
      : {}),
  }), [padding, radius, elevationShadow, blur, opacityTop, opacityBottom, borderAlpha, minWidth, withArrow, offsetY]);

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorReference={anchorEl ? 'anchorEl' : 'anchorPosition'}
      anchorPosition={anchorPosition}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{ paper: { sx: paperSx } }}
    >
      {children}
    </Popover>
  );
}

TransparentPortal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorEl: PropTypes.any,
  anchorPosition: PropTypes.object,
  anchorOrigin: PropTypes.object,
  transformOrigin: PropTypes.object,
  children: PropTypes.node,
  minWidth: PropTypes.number,
  padding: PropTypes.number,
  radius: PropTypes.number,
  blur: PropTypes.number,
  opacityTop: PropTypes.number,
  opacityBottom: PropTypes.number,
  borderAlpha: PropTypes.number,
  elevationShadow: PropTypes.string,
  withArrow: PropTypes.bool,
  offsetY: PropTypes.number,
};

/**
 * TransparentMenu — convenience wrapper around MUI Menu that applies the same glass style.
 * Useful when you want keyboard focus management & list semantics from Menu.
 */
export function TransparentMenu({ open, onClose, anchorEl = null, anchorPosition, children, ...styleProps }) {
  const paperSx = TransparentStyles(styleProps);
  return (
    <Menu
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      anchorReference={anchorEl ? 'anchorEl' : 'anchorPosition'}
      anchorPosition={anchorPosition}
      MenuListProps={{ dense: true, disablePadding: true }}
      PaperProps={{ sx: paperSx }}
    >
      {children}
    </Menu>
  );
}

/** Build the same paper styles outside a component (used by TransparentMenu). */
function TransparentStyles({
  minWidth = 200,
  padding = 0.5,
  radius = 2,
  blur = 16,
  opacityTop = 0.30,
  opacityBottom = 0.14,
  borderAlpha = 0.30,
  elevationShadow = '0 16px 32px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.28)',
} = {}) {
  return {
    p: padding,
    borderRadius: radius,
    boxShadow: elevationShadow,
    backdropFilter: `saturate(140%) blur(${blur}px)`,
    WebkitBackdropFilter: `saturate(140%) blur(${blur}px)`,
    background: `linear-gradient(180deg, rgba(255,255,255,${opacityTop}) 0%, rgba(255,255,255,${opacityBottom}) 100%)`,
    border: '1px solid',
    borderColor: `rgba(255,255,255,${borderAlpha})`,
    backgroundClip: 'padding-box',
    minWidth,
  };
}

/**
 * Convenience building blocks for list-style menus.
 */
export const TPMenuList = React.forwardRef(function TPMenuList(props, ref) {
  const { sx, ...rest } = props;
  return (
    <MenuList
      ref={ref}
      dense
      sx={{
        py: 0.25,
        minWidth: 200,
        '& .MuiListItemButton-root': { borderRadius: 1, transition: 'background-color 120ms ease' },
        '& .MuiListItemButton-root:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
        '& .MuiListItemButton-root:active': { backgroundColor: 'rgba(0,0,0,0.08)' },
        '& .MuiListItemButton-root.Mui-focusVisible': {
          outline: 'none',
          boxShadow: '0 0 0 2px rgba(255,255,255,0.45) inset',
        },
        ...sx,
      }}
      {...rest}
    />
  );
});

export function TPItem({ icon, label, secondary, onClick, disabled, sx, iconSx }) {
  return (
    <ListItemButton onClick={onClick} disabled={disabled} sx={{ py: 0.75, gap: 1, borderRadius: 1, ...sx }}>
      {icon && (
        <ListItemIcon sx={{ minWidth: 32, ...iconSx }}>
          {icon}
        </ListItemIcon>
      )}
      <ListItemText
        primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
        secondaryTypographyProps={{ fontSize: 12, color: 'text.secondary' }}
        primary={label}
        secondary={secondary}
      />
    </ListItemButton>
  );
}

TPItem.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.node,
  secondary: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  iconSx: PropTypes.object,
};

/**
 * Hook: usePortalAnchor — derive anchorPosition from a mouse event.
 */
export function usePortalAnchor() {
  const [anchorPosition, setAnchor] = React.useState();
  const fromMouseEvent = React.useCallback((event) => {
    event.preventDefault?.();
    setAnchor({ top: event.clientY, left: event.clientX });
  }, []);
  return { anchorPosition, setAnchor, fromMouseEvent };
}

export default TransparentPortal;
